import type { AssistantRequest, AssistantResponse } from '../types/assistant';
import { readEventData } from '../utils/eventStream';

const STREAM_REVEAL_INTERVAL_MS = 24;

function isAnswer(value: unknown): value is AssistantResponse {
  if (!value || typeof value !== 'object') return false;
  const answer = value as AssistantResponse;
  return typeof answer.answer === 'string' && Array.isArray(answer.sources) &&
    answer.sources.every(source => typeof source === 'string') &&
    ['schedule', 'documents', 'both', 'general', 'transport', 'calendar'].includes(answer.contextType);
}

function waitForReveal(delayMs: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Give deterministic JSON answers the same progressive reading experience as model streams. */
async function revealImmediateAnswer(
  answer: string,
  onText: (text: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const words = answer.match(/\S+\s*/g) || (answer ? [answer] : []);
  const delayMs = Math.max(6, Math.min(24, Math.round(1_800 / Math.max(1, words.length))));
  let visible = '';
  for (let index = 0; index < words.length; index += 1) {
    signal.throwIfAborted();
    visible += words[index];
    onText(visible);
    if (index < words.length - 1) await waitForReveal(delayMs, signal);
  }
}

export async function requestAssistant(
  request: AssistantRequest,
  onText: (text: string) => void,
  signal: AbortSignal,
  onStatus?: (label: string) => void,
): Promise<AssistantResponse> {
  const response = await fetch('/api/assistant', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(request),
    signal,
  });
  if (!response.ok) {
    throw new Error(response.status === 404
      ? 'Owlet is unavailable right now. Please try again shortly.'
      : 'Owlet could not complete this request. Please try again.');
  }
  // Backwards compatible with older deployments and immediate timetable replies.
  if (!response.headers.get('content-type')?.includes('text/event-stream')) {
    const answer: unknown = await response.json().catch(() => null);
    if (!isAnswer(answer)) throw new Error('Owlet returned an invalid response. Please try again.');
    await revealImmediateAnswer(answer.answer, onText, signal);
    return answer;
  }
  if (!response.body) throw new Error('Owlet returned an empty response.');
  let text = '';
  let lastRevealAt = 0;
  for await (const data of readEventData(response.body, signal)) {
    let event;
    try { event = JSON.parse(data); }
    catch { throw new Error('The response was interrupted. Please try again.'); }
    if (!event || typeof event !== 'object' || Array.isArray(event)) {
      throw new Error('The response was interrupted. Please try again.');
    }
    if (event.type === 'delta' && typeof event.text === 'string') {
      const waitMs = lastRevealAt
        ? Math.max(0, STREAM_REVEAL_INTERVAL_MS - (Date.now() - lastRevealAt))
        : 0;
      if (waitMs) await waitForReveal(waitMs, signal);
      text += event.text;
      if (text.length > 100_000) throw new Error('Owlet response is too long.');
      onText(text);
      lastRevealAt = Date.now();
    } else if (event.type === 'status' && typeof event.label === 'string' && event.label.length <= 100) {
      onStatus?.(event.label);
    } else if (event.type === 'done' && isAnswer(event.response)) {
      // The final answer can replace partial text with a complete source-data fallback.
      return event.response;
    } else if (event.type === 'error') {
      throw new Error('Owlet lost the connection. Please try again.');
    }
  }
  throw new Error('The response was interrupted. Please try again.');
}
